import {
  asMetadataFieldId,
  MAX_METADATA_FIELDS,
  type SignalMetadataFieldType,
  type SignalMetadataSchema,
  type SignalMetadataSchemaField,
} from "@repo/app-types/entities/signal";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { IconPlus } from "@tabler/icons-react";
import type { FC } from "react";
import { v4 as uuidv4 } from "uuid";

import { MetadataFieldRow } from "./metadataFieldRow";

function createEmptyField(): SignalMetadataSchemaField {
  return {
    id: asMetadataFieldId(uuidv4()),
    key: "",
    type: "enum",
    options: [""],
  };
}

export interface MetadataSchemaBuilderProps {
  value: SignalMetadataSchema;
  onChange: (value: SignalMetadataSchema) => void;
  isDisabled?: boolean;
}

export const MetadataSchemaBuilder: FC<MetadataSchemaBuilderProps> = ({
  value,
  onChange,
  isDisabled = false,
}) => {
  const handleAddField = () => {
    if (value.fields.length >= MAX_METADATA_FIELDS) return;
    onChange({ fields: [...value.fields, createEmptyField()] });
  };

  const handleRemoveField = (index: number) => {
    const updated = [...value.fields];
    updated.splice(index, 1);
    onChange({ fields: updated });
  };

  const handleFieldKeyChange = (index: number, key: string) => {
    const updated = [...value.fields];
    const field = updated[index];
    if (field === undefined) return;
    updated[index] = { ...field, key };
    onChange({ fields: updated });
  };

  const handleFieldTypeChange = (
    index: number,
    type: SignalMetadataFieldType,
  ) => {
    const updated = [...value.fields];
    const existing = updated[index];
    if (existing === undefined) return;
    // Preserve the stable id so previously extracted metadata stays linked
    // even if the user flips the field type.
    if (type === "enum") {
      updated[index] = {
        id: existing.id,
        key: existing.key,
        type: "enum",
        options: [""],
      };
    } else {
      updated[index] = {
        id: existing.id,
        key: existing.key,
        type: "number",
      };
    }
    onChange({ fields: updated });
  };

  const handleOptionChange = (
    fieldIndex: number,
    optionIndex: number,
    optionValue: string,
  ) => {
    const updated = [...value.fields];
    const field = updated[fieldIndex];
    if (field === undefined || field.type !== "enum") return;
    const newOptions = [...field.options];
    newOptions[optionIndex] = optionValue;
    updated[fieldIndex] = { ...field, options: newOptions };
    onChange({ fields: updated });
  };

  const handleAddOption = (fieldIndex: number) => {
    const updated = [...value.fields];
    const field = updated[fieldIndex];
    if (field === undefined || field.type !== "enum") return;
    updated[fieldIndex] = { ...field, options: [...field.options, ""] };
    onChange({ fields: updated });
  };

  const handleRemoveOption = (fieldIndex: number, optionIndex: number) => {
    const updated = [...value.fields];
    const field = updated[fieldIndex];
    if (field === undefined || field.type !== "enum") return;
    const newOptions = [...field.options];
    newOptions.splice(optionIndex, 1);
    updated[fieldIndex] = { ...field, options: newOptions };
    onChange({ fields: updated });
  };

  return (
    <div className="space-y-3">
      <Label size={Size.SMALL}>Fields</Label>
      <p className="text-sm text-muted-foreground">
        Define up to {MAX_METADATA_FIELDS} metadata fields to extract for each
        match. Use text for categorical values and number for numeric values.
      </p>

      {value.fields.map((field, fieldIndex) => (
        <MetadataFieldRow
          key={field.id}
          field={field}
          onKeyChange={(key) => handleFieldKeyChange(fieldIndex, key)}
          onTypeChange={(type) => handleFieldTypeChange(fieldIndex, type)}
          onOptionChange={(optionIndex, optionValue) =>
            handleOptionChange(fieldIndex, optionIndex, optionValue)
          }
          onAddOption={() => handleAddOption(fieldIndex)}
          onRemoveOption={(optionIndex) =>
            handleRemoveOption(fieldIndex, optionIndex)
          }
          onRemove={() => handleRemoveField(fieldIndex)}
          isDisabled={isDisabled}
        />
      ))}

      <Button
        variant={ButtonVariant.Secondary}
        icon={<IconPlus className="size-4" />}
        onClick={handleAddField}
        isDisabled={isDisabled || value.fields.length >= MAX_METADATA_FIELDS}
      >
        Add field
      </Button>
    </div>
  );
};
